import { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Autoplay, Pagination, Navigation, Keyboard } from 'swiper/modules';
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { siteConfig } from '@/config/site';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './VixenCarousel.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Casa de Candy — single immersive coverflow stage.
 * Slides melt into the outer box via edge fade gradients (no nested cards).
 * Title sits bottom-left, description bottom-center inside the same box.
 */
export function VixenCarousel() {
  const [slides, setSlides] = useState([]);
  const [music, setMusic] = useState(null);
  const [muted, setMuted] = useState(true);
  const [active, setActive] = useState(0);
  const audioRef = useRef(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [vRes, mRes, musRes] = await Promise.all([
          fetch(`${API}/api/vixens`).then((r) => r.json()),
          fetch(`${API}/api/media`).then((r) => r.json()),
          fetch(`${API}/api/music`).then((r) => r.json()),
        ]);
        if (cancelled) return;
        const vixenSlides = (vRes.items || []).map((v) => ({
          id: `v-${v.id}`,
          kind: 'image',
          src: `${API}${v.photo_url}`,
          title: v.name,
          bio: v.bio || '',
        }));
        const mediaSlides = (mRes.items || []).map((m) => ({
          id: `m-${m.id}`,
          kind: m.kind,
          src: `${API}${m.url}`,
          title: m.title || '',
          bio: '',
        }));
        const all = [...vixenSlides, ...mediaSlides];
        // Quiet fallback — branded poster (no promotional copy) when admins
        // haven't uploaded any content yet.
        if (all.length === 0) {
          all.push({
            id: 'fallback-brand',
            kind: 'brand',
            src: null,
            title: '',
            bio: '',
            isFallback: true,
          });
        }
        setSlides(all);
        if (musRes && musRes.url) {
          setMusic({ url: `${API}${musRes.url}`, title: musRes.title });
        }
      } catch (_e) {
        if (!cancelled) {
          setSlides([
            {
              id: 'fallback-brand',
              kind: 'brand',
              src: null,
              title: '',
              bio: '',
              isFallback: true,
            },
          ]);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    if (muted) {
      a.volume = 0.35;
      a.play().catch(() => {});
      setMuted(false);
    } else {
      a.pause();
      setMuted(true);
    }
  };

  const activeSlide = slides[active] || slides[0];
  // Reserved — keep `active` state hooked so future overlays can react to
  // slide changes without re-wiring Swiper. Currently the editorial
  // casting card embeds its own name (.vixen-coverflow__card-name).
  void activeSlide;

  return (
    <div className="vixen-coverflow" data-testid="vixen-carousel">
      <div className="vixen-coverflow__stage">
        {/* Edge fade — left/right gradient mask wrapping the full outer box */}
        <div className="vixen-coverflow__edge vixen-coverflow__edge--left" aria-hidden />
        <div className="vixen-coverflow__edge vixen-coverflow__edge--right" aria-hidden />

        <Swiper
          onSwiper={(s) => (swiperRef.current = s)}
          onSlideChange={(s) => setActive(s.realIndex)}
          modules={[EffectCoverflow, Autoplay, Pagination, Navigation, Keyboard]}
          effect="coverflow"
          grabCursor
          centeredSlides
          slidesPerView="auto"
          loop={slides.length > 1}
          keyboard={{ enabled: true }}
          coverflowEffect={{
            rotate: 32,
            stretch: 0,
            depth: 220,
            modifier: 1,
            slideShadows: false,
          }}
          autoplay={
            slides.length > 1
              ? { delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }
              : false
          }
          pagination={{ clickable: true, dynamicBullets: true }}
        >
          {slides.map((s) => (
            <SwiperSlide key={s.id} className="vixen-coverflow__slide">
              {s.kind === 'video' ? (
                <video
                  src={s.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="vixen-coverflow__media"
                />
              ) : s.kind === 'brand' ? (
                <div className="vixen-coverflow__brand">
                  <div className="vixen-coverflow__brand-halo" aria-hidden />
                  <img
                    src={siteConfig.brand.logoUrl}
                    alt="Casa de Candy"
                    className="vixen-coverflow__brand-logo animate-breathe"
                  />
                </div>
              ) : (
                <>
                  <img
                    src={s.src}
                    alt={s.title || 'Casa de Candy'}
                    className="vixen-coverflow__media"
                    loading="lazy"
                  />
                  {/* Bottom gradient overlay — keeps title readable
                      without obscuring the image. Editorial casting-card
                      treatment, opacity tuned to be subtle. */}
                  <div
                    className="vixen-coverflow__card-veil"
                    aria-hidden="true"
                  />
                  {s.title && (
                    <div className="vixen-coverflow__card-name">
                      {s.title}
                    </div>
                  )}
                </>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Editorial casting cards display the name inside each card via
            .vixen-coverflow__card-name. The stage-level title overlay was
            removed to keep the experience image-first and uncluttered. */}

        {slides.length > 1 && (
          <>
            <button
              type="button"
              className="vixen-coverflow__nav vixen-coverflow__nav--prev"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous"
              data-testid="vixen-carousel-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="vixen-coverflow__nav vixen-coverflow__nav--next"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next"
              data-testid="vixen-carousel-next"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {music && (
          <button
            type="button"
            className="vixen-coverflow__music"
            onClick={toggleMute}
            aria-label={muted ? 'Play background music' : 'Mute background music'}
            data-testid="vixen-carousel-music-toggle"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        )}
        {music && <audio ref={audioRef} src={music.url} loop preload="auto" />}
      </div>
    </div>
  );
}

export default VixenCarousel;
